import React, { Component, RefObject } from "react";

interface IDraggableCoverProps {
  onDragStop?: (data: any) => any;
  disabled: boolean;
  children: (props: IDraggableCoverChildren) => any;
}

interface IDraggableCoverChildren {
  top: number;
  isDragging: boolean;
  coverRef: RefObject<HTMLImageElement>;
  parentRef: RefObject<HTMLElement>;
  events: IEvents;
  style: any;
  cropState: (y: number) => any;
}

interface IEvents {
  onMouseDown: (event: Event) => any;
}

interface IDraggrableCoverState {
  y: number;
  isDragging: boolean;
  startingX: number | null;
  startingY: number | null;
}

export default class DraggableCover extends Component<IDraggableCoverProps, IDraggrableCoverState> {
  state = {
    y: 0,
    isDragging: false,
    startingX: null,
    startingY: null,
  };

  coverRef = React.createRef<HTMLImageElement>();
  parentRef = React.createRef<HTMLElement>();
  coverRect: any = null;
  parentRect: any = null;

  handleMouseDown = (event: Event) => {
    const { disabled } = this.props;

    if (disabled) {
      return;
    }

    this.coverRect = (this.coverRef.current as HTMLImageElement).getBoundingClientRect();
    this.parentRect = (this.parentRef.current as HTMLElement).getBoundingClientRect();

    const { x, y } = this.getCoordinates(event);

    this.setState({
      startingX: x - (this.coverRect.left - this.parentRect.left),
      startingY: y - (this.coverRect.top - this.parentRect.top),
    });

    document.addEventListener("mouseup", this.handleStopDragging);
    document.addEventListener("mousemove", this.handleStartDragging);
  };

  handleStartDragging: EventListener = (event) => {
    const { x, y } = this.getCoordinates(event);
    const { startingX, startingY } = this.state;

    const a = Math.abs(Number(startingX) - x);
    const b = Math.abs(Number(startingY) - y);

    const distance = Math.round(Math.sqrt(Math.pow(a, 2) + Math.pow(b, 2)));

    if (distance >= 10) {
      this.handleStopDragging();
      this.handleDrag();
    }
  };

  handleStopDragging = () => {
    document.removeEventListener("mouseup", this.handleStopDragging);
    document.removeEventListener("mousemove", this.handleStartDragging);
  };

  handleDrag = () => {
    this.setState({
      isDragging: true,
    });

    window.addEventListener("mouseup", this.handleStopDrag);
    window.addEventListener("mousemove", this.handleMouseMove);
  };

  handleStopDrag = () => {
    this.setState({
      startingY: null,
      isDragging: false,
    });

    window.removeEventListener("mouseup", this.handleStopDrag);
    window.removeEventListener("mousemove", this.handleMouseMove);
  };

  calculateCropState = (y: number): any | null => {
    if (this.coverRef.current && this.parentRef.current) {
      return {
        x: 0,
        y: Math.abs(y),
        original: {
          width: (this.coverRef.current as HTMLImageElement).naturalWidth,
          height: (this.coverRef.current as HTMLImageElement).naturalHeight,
        },
        proportional: {
          width: this.parentRef.current.clientWidth,
          height: Math.round(
            ((this.coverRef.current as HTMLImageElement).naturalHeight /
              (this.coverRef.current as HTMLImageElement).naturalWidth) *
              this.parentRef.current.clientWidth,
          ),
        },
        rect: {
          width: this.parentRef.current.clientWidth,
          height: this.parentRef.current.clientHeight,
        },
      };
    } else {
      return null;
    }
  };

  handleMouseMove: EventListener = (event) => {
    const { y } = this.getCoordinates(event);
    const { onDragStop } = this.props;

    this.setState(
      ({ startingY }) => ({
        y: this.calculateY(y, Number(startingY)),
      }),
      () => {
        if (onDragStop) {
          onDragStop(this.calculateCropState(this.state.y as number));
        }
      },
    );
  };

  calculateY = (clientY: number, startingY: number) => {
    const y = clientY - startingY;
    const y1 = Math.round((this.parentRef.current as HTMLElement).clientHeight);
    const y2 = Math.round((this.coverRef.current as HTMLImageElement).height);

    if (y >= 0) {
      return 0;
    } else if (y <= y1 - y2) {
      return y1 - y2;
    } else {
      return y;
    }
  };

  getCoordinates = (event: Event) => {
    let x, y;

    if ("ontouchstart" in window && (event as TouchEvent).touches) {
      x = (event as TouchEvent).touches[0].clientX;
      y = (event as TouchEvent).touches[0].clientY;
    } else {
      x = (event as MouseEvent).clientX;
      y = (event as MouseEvent).clientY;
    }

    return {
      x,
      y,
    };
  };

  getStyleForRender = (y: number, disabled: boolean) => {
    if (disabled) {
      return {
        pointerEvents: "none",
        userSelect: "none",
        cursor: "default",
      };
    } else {
      return {
        top: y,
        userSelect: "none",
        position: "absolute",
        cursor: "row-resize",
      };
    }
  };

  render() {
    const { disabled, children } = this.props;
    const { y, isDragging } = this.state;

    return children({
      top: y,
      isDragging,
      coverRef: this.coverRef,
      parentRef: this.parentRef,
      events: {
        onMouseDown: this.handleMouseDown,
      },
      style: this.getStyleForRender(y, disabled),
      cropState: this.calculateCropState(y),
    });
  }
}
